<script lang="ts">
import type { RegisteredComponent } from '@builder.io/sdk-svelte';
import { Content, isPreviewing } from '@builder.io/sdk-svelte';
import { CUSTOM_COMPONENTS } from '../../../CustomComponents.js';
import { BUILDER_PUBLIC_API_KEY } from '../../../config/apiKeys.js';
import { EHeroAspectRatios, ERoutes, ETitleFontSizes } from '../../../config/enums.js';
import CardGridItem from '../../../stories/atoms/CardGridItem/CardGridItem.svelte';
import SectionHeader from '../../../stories/atoms/SectionHeader/SectionHeader.svelte';
import Breadcrumb from '../../../stories/molecules/Breadcrumb/Breadcrumb.svelte';
import CardGrid from '../../../stories/molecules/CardGrid/CardGrid.svelte';
import WorkOverview from '../../../stories/molecules/WorkOverview/WorkOverview.svelte';
import Hero from '../../../stories/organisms/Hero/Hero.svelte';

interface BreadcrumbItem {
    text: string;
    to: string;
    active: boolean;
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

// shortcut to the actual content
let pageContent = $derived(data.builderProps.content?.data || {});
// we want to show unpublished content when in preview mode.
let canShowContent = $derived(pageContent || isPreviewing());

let breadCrumbList: BreadcrumbItem[] = $state([]);

let currentSlug = '';

$effect(() => {
    if (pageContent.slug !== currentSlug) {
        breadCrumbList.push({
            text: pageContent?.clientName ?? '',
            to: `${ERoutes.Work}${pageContent.slug}`,
            active: true,
        });
        breadCrumbList.splice(0, 0, {
            text: 'Showcase',
            to: ERoutes.Work,
            active: false,
        });
        currentSlug = pageContent.slug;
    }
});
</script>

{#if canShowContent}
    <!-- force re-rendering of `Content` on client-side navigation -->
    {#key pageContent}
        {#if pageContent?.mastheadImage || pageContent?.mastheadVideo}
            <Hero
                image={{
                    src: pageContent?.mastheadImage,
                    alt: pageContent?.clientName ?? '',
                }}
                mobileImage={{
                    src: pageContent?.mastheadImageMobile || pageContent?.mastheadImage,
                    alt: pageContent?.clientName ?? '',
                }}
                muxId={pageContent?.muxId}
                muxIdMobile={pageContent?.muxIdMobile}
                poster={pageContent?.mastheadImage}
                posterMobile={pageContent?.mastheadImageMobile || pageContent?.mastheadImage}
                content={pageContent?.clientName ?? ''}
                titleSize={ETitleFontSizes.H2}
                mastheadTextColour={pageContent?.mastheadTextColour}
                aspectRatio={EHeroAspectRatios.SHOWCASE}
            />
        {/if}
        <div class="scroll-content-over-header">
            <Breadcrumb items={breadCrumbList} />
            <WorkOverview
                clientUrl={pageContent?.clientUrl ?? ''}
                text={pageContent?.introCopy ?? ''}
                serviceItems={pageContent.servicesProvided}
                industriesItems={pageContent.industries}
            />
            <!-- Render builder content with all required props -->
            <div class="container builder-content container--work-page">
                <Content
                    model="work-page"
                    content={data.builderProps.content}
                    apiKey={BUILDER_PUBLIC_API_KEY}
                    customComponents={CUSTOM_COMPONENTS as RegisteredComponent[]}
                />
            </div>

            {#if pageContent?.relatedCaseStudy1?.value.data.slug || pageContent?.relatedCaseStudy2?.value.data.slug}
                <div class="related-container">
                    <SectionHeader titleSize="h3" includeGutter={true}
                        >Related Projects</SectionHeader
                    >
                    <CardGrid hasBorder={false} hasBottomMargin={false}>
                        {#snippet children()}
                            {#if pageContent?.relatedCaseStudy1?.value.data.slug}
                                <CardGridItem
                                    class="builder-block"
                                    to={ERoutes.Work +
                                        pageContent.relatedCaseStudy1.value.data
                                            .slug}
                                    title={pageContent.relatedCaseStudy1.value
                                        .data.clientName}
                                    image={{
                                        src: pageContent.relatedCaseStudy1.value
                                            .data.clientThumbnail,
                                        alt: pageContent.relatedCaseStudy1.value
                                            .data.clientName,
                                    }}
                                    key="1"
                                />
                            {/if}
                            {#if pageContent?.relatedCaseStudy2?.value.data.slug}
                                <CardGridItem
                                    class="builder-block"
                                    to={ERoutes.Work +
                                        pageContent.relatedCaseStudy2.value.data
                                            .slug}
                                    title={pageContent.relatedCaseStudy2.value
                                        .data.clientName}
                                    image={{
                                        src: pageContent.relatedCaseStudy2.value
                                            .data.clientThumbnail,
                                        alt: pageContent.relatedCaseStudy2.value
                                            .data.clientName,
                                    }}
                                    key="2"
                                />
                            {/if}
                        {/snippet}
                    </CardGrid>
                </div>
            {/if}
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .scroll-content-over-header {
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

    .related-container {
        margin-top: 5rem;

        @include mq($from: tablet) {
            margin-top: 10rem;
        }

        :global(.section-header) {
            margin-bottom: 3rem;

            @include mq($from: tablet) {
                margin-bottom: 4rem;
            }
        }
    }

    :global(.builder-block:has(> picture)) {
        margin: var(--layout-pagegrid-margin);
        width: auto;
    }
</style>
